import { useState } from 'react';
import '../../assets/css/case-home.css';
import type { ComponentContentSchema } from '@vibesites/runtime';
// @ts-ignore
import { useContentToken, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlassPlus } from '@fortawesome/free-solid-svg-icons';
import { PhotoLightbox } from './PhotoLightbox';

/** Bump a CDN image to a higher resolution for the lightbox so fine print stays sharp. */
function awardHiRes(url: string): string {
  if (!url) return url;
  return url.replace(/\/cdn-cgi\/image\/[^/]*\//, '/cdn-cgi/image/format=auto,w=1600/');
}

export function AwardsIntro() {
  const title = useContentToken('awardsintro.title');
  const ctaText = useContentToken('awardsintro.cta_text');
  const ctaUrl = useContentToken('awardsintro.cta_url');
  const image0 = useContentToken('awardsintro.image_0');
  const image1 = useContentToken('awardsintro.image_1');
  const award0 = useContentToken('awardsintro.award_0');
  const award1 = useContentToken('awardsintro.award_1');
  const recognitionEyebrow = useContentToken('awardsintro.recognition_eyebrow');
  const recognitionTitle = useContentToken('awardsintro.recognition_title');
  const award0Title = useContentToken('awardsintro.award_0_title');
  const award0Caption = useContentToken('awardsintro.award_0_caption');
  const award0Source = useContentToken('awardsintro.award_0_source');
  const award1Title = useContentToken('awardsintro.award_1_title');
  const award1Caption = useContentToken('awardsintro.award_1_caption');
  const award1Source = useContentToken('awardsintro.award_1_source');

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const openLightbox = (i: number) => { setLightboxIndex(i); setLightboxOpen(true); };
  const awardPhotos = [
    { src: awardHiRes(award0 as string), alt: 'Award' },
    { src: awardHiRes(award1 as string), alt: 'Award' },
  ];

  return (
    <section className="case-awards-intro">
      <div className="case-container">
        <div className="case-awards-intro-text">
          <RichTextContent
            path="awardsintro.body"
            label="Intro Text"
            defaultValue={contentSchema.tokens['awardsintro.body'].defaultValue}
            as="p"
            style={{ fontSize: '1rem', lineHeight: 1.6, marginBottom: '1.5rem' }}
          />
          <EditableToken path="awardsintro.cta_url" type="url" label="CTA URL" defaultValue={contentSchema.tokens['awardsintro.cta_url'].defaultValue} as="span" style={{ display: 'contents' }}>
            <EditableToken path="awardsintro.cta_text" type="text" label="CTA Button Text" defaultValue={contentSchema.tokens['awardsintro.cta_text'].defaultValue} as="span" style={{ display: 'contents' }}>
              <a className="case-btn" href={ctaUrl}>{ctaText}</a>
            </EditableToken>
          </EditableToken>
        </div>
      </div>

      <div className="case-awards-recognition">
        <div className="case-container">
          <EditableToken
            path="awardsintro.recognition_eyebrow"
            type="text"
            label="Recognition Eyebrow"
            defaultValue={contentSchema.tokens['awardsintro.recognition_eyebrow'].defaultValue}
            as="p"
            className="case-awards-recognition-eyebrow"
          >
            {recognitionEyebrow}
          </EditableToken>
          <EditableToken
            path="awardsintro.recognition_title"
            type="text"
            label="Recognition Title"
            defaultValue={contentSchema.tokens['awardsintro.recognition_title'].defaultValue}
            as="h2"
            className="case-awards-recognition-title"
          >
            {recognitionTitle}
          </EditableToken>
          <div className="case-awards-badges">
            <div className="case-awards-badge-item">
              <EditableToken path="awardsintro.award_0" type="image" label="Award Badge 1" defaultValue={contentSchema.tokens['awardsintro.award_0'].defaultValue} as="div" className="case-awards-badge">
                <button type="button" className="case-awards-badge-btn" onClick={() => openLightbox(0)} aria-label="View award larger">
                  <VibesitesImage url={award0 as string} alt="Award" width={300} height={300} style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }} />
                  <span className="case-awards-badge-zoom"><FontAwesomeIcon icon={faMagnifyingGlassPlus} /></span>
                </button>
              </EditableToken>
              <EditableToken path="awardsintro.award_0_title" type="text" label="Award 1 Title" defaultValue={contentSchema.tokens['awardsintro.award_0_title'].defaultValue} as="h3" className="case-awards-badge-name">
                {award0Title}
              </EditableToken>
              <EditableToken path="awardsintro.award_0_caption" type="text" label="Award 1 Caption" defaultValue={contentSchema.tokens['awardsintro.award_0_caption'].defaultValue} as="p" className="case-awards-badge-sub">
                {award0Caption}
              </EditableToken>
              <EditableToken path="awardsintro.award_0_source" type="text" label="Award 1 Source" defaultValue={contentSchema.tokens['awardsintro.award_0_source'].defaultValue} as="p" className="case-awards-badge-source">
                {award0Source}
              </EditableToken>
            </div>
            <div className="case-awards-badge-item">
              <EditableToken path="awardsintro.award_1" type="image" label="Award Badge 2" defaultValue={contentSchema.tokens['awardsintro.award_1'].defaultValue} as="div" className="case-awards-badge">
                <button type="button" className="case-awards-badge-btn" onClick={() => openLightbox(1)} aria-label="View award larger">
                  <VibesitesImage url={award1 as string} alt="Award" width={300} height={300} style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }} />
                  <span className="case-awards-badge-zoom"><FontAwesomeIcon icon={faMagnifyingGlassPlus} /></span>
                </button>
              </EditableToken>
              <EditableToken path="awardsintro.award_1_title" type="text" label="Award 2 Title" defaultValue={contentSchema.tokens['awardsintro.award_1_title'].defaultValue} as="h3" className="case-awards-badge-name">
                {award1Title}
              </EditableToken>
              <EditableToken path="awardsintro.award_1_caption" type="text" label="Award 2 Caption" defaultValue={contentSchema.tokens['awardsintro.award_1_caption'].defaultValue} as="p" className="case-awards-badge-sub">
                {award1Caption}
              </EditableToken>
              <EditableToken path="awardsintro.award_1_source" type="text" label="Award 2 Source" defaultValue={contentSchema.tokens['awardsintro.award_1_source'].defaultValue} as="p" className="case-awards-badge-source">
                {award1Source}
              </EditableToken>
            </div>
          </div>
        </div>
      </div>

      <div className="case-container">
        <div className="case-awards-culture-grid">
          <div className="case-awards-culture-images">
            <EditableToken path="awardsintro.image_0" type="image" label="Awards Image 1" defaultValue="" as="div" className="case-awards-image-slot">
              <VibesitesImage url={image0 as string} alt="" width={400} height={450} style={{ aspectRatio: '8/9' }} />
            </EditableToken>
            <EditableToken path="awardsintro.image_1" type="image" label="Awards Image 2" defaultValue="" as="div" className="case-awards-image-slot">
              <VibesitesImage url={image1 as string} alt="" width={400} height={450} style={{ aspectRatio: '8/9' }} />
            </EditableToken>
          </div>
          <div className="case-awards-culture-text">
            <EditableToken
              path="awardsintro.title"
              type="text"
              label="Section Title"
              defaultValue={contentSchema.tokens['awardsintro.title'].defaultValue}
              as="h2"
              style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '1.5rem', textTransform: 'uppercase' }}
            >
              {title}
            </EditableToken>
            <RichTextContent
              path="awardsintro.culture_body"
              label="Culture of Excellence Text"
              defaultValue={contentSchema.tokens['awardsintro.culture_body'].defaultValue}
              as="div"
            />
          </div>
        </div>
      </div>

      <PhotoLightbox
        photos={awardPhotos}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        overlayClassName="case-awards-lightbox"
      />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AwardsIntro',
  displayName: 'Awards Intro',
  tokens: {
    'awardsintro.body': {
      label: 'Intro Text',
      type: 'richtext',
      defaultValue: 'Our philosophy of providing five-star personalized service has proven to be effective not only to our success as a company, but to the satisfaction of our clients and residents. At Case &amp; Associates, we\u2019re proud of the awards we\u2019ve received because it shows that our vision of caring is improving the lives of <a href="/careers/">our employees</a> and the people we serve \u2013 it\u2019s our mission. And it\u2019s what drives our success.',
    },
    'awardsintro.cta_text': {
      label: 'CTA Button Text',
      type: 'text',
      defaultValue: 'View Our Communities',
    },
    'awardsintro.cta_url': {
      label: 'CTA URL',
      type: 'url',
      defaultValue: '/apartments/',
    },
    'awardsintro.title': {
      label: 'Section Title',
      type: 'text',
      defaultValue: 'A Culture of Excellence',
    },
    'awardsintro.culture_body': {
      label: 'Culture of Excellence Text',
      type: 'richtext',
      defaultValue: '<p>We strive to constantly improve our methods of providing superior service as needs shift and times change. In 2020 we were rewarded for our efforts with the distinction of being a <strong>Division II Top 10 Management Company of the Year</strong>, one of the most prestigious awards in the industry by ORA\u2122 Power Rankings and J Turner Research.</p><p>The pride of being a best-in-class management company runs through every division and level of our organization. Every day we are driven to do better for our customers and team members. Take a look at our many awards and see how Case &amp; Associates is changing the real estate management industry <a href="/apartments/">one property</a> at a time.</p>',
    },
    'awardsintro.image_0': {
      label: 'Awards Image 1',
      type: 'image',
      defaultValue: '',
    },
    'awardsintro.image_1': {
      label: 'Awards Image 2',
      type: 'image',
      defaultValue: '',
    },
    'awardsintro.recognition_eyebrow': {
      label: 'Recognition Eyebrow',
      type: 'text',
      defaultValue: '2025 Recognition',
    },
    'awardsintro.recognition_title': {
      label: 'Recognition Title',
      type: 'text',
      defaultValue: 'Top-Rated Management Company',
    },
    'awardsintro.award_0': {
      label: 'Award Badge 1',
      type: 'image',
      defaultValue: 'https://multihubmedia.com/cdn-cgi/image/format=auto,w=800/company/5767906449162419/019e8e6e-96a3-7082-8177-d2c3b79c38be.webp',
    },
    'awardsintro.award_0_title': {
      label: 'Award 1 Title',
      type: 'text',
      defaultValue: 'ORA® Power Rankings',
    },
    'awardsintro.award_0_caption': {
      label: 'Award 1 Caption',
      type: 'text',
      defaultValue: 'Division II Top 10 by online reputation',
    },
    'awardsintro.award_0_source': {
      label: 'Award 1 Source',
      type: 'text',
      defaultValue: '— J Turner Research',
    },
    'awardsintro.award_1': {
      label: 'Award Badge 2',
      type: 'image',
      defaultValue: 'https://multihubmedia.com/cdn-cgi/image/format=auto,w=800/company/5767906449162419/019e8eb5-d989-75df-8098-a0a348293e40.webp',
    },
    'awardsintro.award_1_title': {
      label: 'Award 2 Title',
      type: 'text',
      defaultValue: 'Resident Choice Award',
    },
    'awardsintro.award_1_caption': {
      label: 'Award 2 Caption',
      type: 'text',
      defaultValue: 'Premier Operator, 4-category winner',
    },
    'awardsintro.award_1_source': {
      label: 'Award 2 Source',
      type: 'text',
      defaultValue: '— Opiniion, 2025',
    },
  },
};

(AwardsIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AwardsIntro;
